/**
 * @author Muhammad Fahad
 * @datetime April, 14th 2022
 * @description: This class is for utility operations.
 */
import { format, parse } from 'date-fns';
import { ElementRef, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { isEqual } from 'lodash';
import { PermanantStateManagement } from '../models/types';
import { PageName } from './user-roles';
import { CONSTANTS as CONST } from 'src/app/shared/constants/constants';
import { config as config } from 'src/config/config';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { IUserGroups } from 'src/app/user-management/add-user/add-user-management.interface';
import { UserCards } from 'src/app/shared-layout/Components/activity/user-card.interface';
import {
  ActivityType,
  IActivityType,
} from 'src/app/shared-layout/Components/activity/locks-type';
import { utcToZonedTime } from 'date-fns-tz';
import { TranslateService } from '@ngx-translate/core';
import { enUS, fr } from 'date-fns/locale';
import { StateManagementService } from '../../shared/services/state-management.service';
@Injectable({
  providedIn: 'root',
})
export class Utility {
  readonly CONST = CONST;
  readonly config = config;
  readonly LANG_CONST = LANG_CONST;
  /**
   * @description initializes services
   */
  constructor(
    private translateService: TranslateService,
    private stateManagementService: StateManagementService
  ) {}

  /**
   * @description this method compared two JSON objects
   * @param { JSON } obj
   * @param { JSON } obj2
   * @returns { boolean }
   */
  static compareJson(obj: Object, obj2: Object) {
    return isEqual(obj, obj2);
  }

  static compareTwoStoreStates(arr1: any, arr2: any) {
    if (arr1?.length === arr2?.length) {
      for (let i = 0; i < arr1.length; i++) {
        if (!Utility.compareJson(arr1[i], arr2[i])) {
          return false; // If any element is not equal, return false
        }
      }
      return true; // If all elements are equal, return true
    } else {
      return false; // If the lengths are different, return false
    }
  }

  static compareTwoStoreJsonStates(state1: any, state2: any) {
    if (JSON.stringify(state1) !== JSON.stringify(state2)) return true;
    else return false;
  }

  /**
   * @description this method manages adding and removing head in request
   * @param { boolean } loader
   * @returns
   */
  static loaderHeader(loader: boolean | undefined): PermanantStateManagement {
    return loader ? { loader: 'true' } : {};
  }

  /**
   * @description this method checks if TTL for chache is expired or not
   * @param { number } ttl
   * @param { number } initialTime
   * @returns
   */
  static checkTTLExpiry(ttl: number, initialTime: number) {
    const currentTime = Date.now() - initialTime;
    return currentTime < ttl;
  }

  /**
   * @description this is validator method for validating white spaces
   * @param { FormControl } control
   * @returns
   */
  static noWhitespaceValidator(control: FormControl) {
    if (!control.value || control.value?.length === 0) return null;
    const valueLength = (control.value || '').trim().length;
    const isValid = valueLength === control.value.length;
    return isValid ? null : { whitespace: true };
  }

  /**
   * @description ##alphaNumeric is validator method for validating form field to
   *              have only numbers and alphabets
   * @param { FormControl } control
   * @returns
   */

  static alphaNumeric(control: FormControl): { invalidName: true } | null {
    const ALPHA_NUMERIC_REGEXP = /^[A-Za-z0-9 ]+$/;
    if (
      control?.value?.length === 0 ||
      ALPHA_NUMERIC_REGEXP.test(control?.value)
    ) {
      return null;
    }
    return {
      invalidName: true,
    };
  }

  /**
   * @description ##specialAddressCharacters is validator method for validating form field to
   *              have only address typed special characters
   * @param { FormControl } control
   * @returns
   */

  static specialAddressCharacters(
    control: FormControl
  ): { invalidAddress: true } | null {
    const ADDRES_CHARACTER_REGEXP = /^[A-Za-z0-9 ',.%&/#@-]+$/;
    if (
      control?.value?.length === 0 ||
      ADDRES_CHARACTER_REGEXP.test(control?.value)
    ) {
      return null;
    }

    return {
      invalidAddress: true,
    };
  }

  /**
   * @description this method checks if uploading file is valid image file
   * @param { string } filename
   * @returns
   */
  static checkValidImageExtension(filename: string) {
    const validExtensions = ['jpg', 'png', 'jpeg'];
    const result = filename?.split('.');
    const ext = result.pop();
    return validExtensions.includes(ext?.toLowerCase() || '');
  }

  /**
   * @description this method checks if uploading file size is valid image file size 25 MB.
   * @param { number } fileSize
   * @param { number } defaultfileSize
   * @returns { boolean }
   */
  static checkValidImageSize(fileSize: number, maxFileSize: number) {
    if (fileSize > maxFileSize * 1024 * 1024) return true;
  }

  /**
   * @description this method checks if uploading file size is valid image file size 25 MB.
   * @param { number } fileSize
   * @param { number } defaultfileSize
   * @returns { boolean }
   */
  static checkValidValue(value: string) {
    return !(value === '' || value === null || value === undefined);
  }

  /**
   * @description this method encodes into base 64
   * @param { string } data data sent for encoding
   * @returns { string }
   */
  static encodeToBase64(data: string): string {
    const encoded = btoa(data);
    return encoded;
  }

  /**
   * @description this method decodes from base 64
   * @param { string } data data sent for decoding
   * @returns { string }
   */
  static decodeFromBase64(data: string): string {
    const decoded = atob(data);
    return decoded ?? '';
  }

  /**
   * @description numberInString method accepts any value and return a 1st number present in
   * @param { data }
   * @returns { string }
   */
  static numberInString(data: any) {
    return data?.match(/\d+/)[0];
  }

  /**
   * @description: This method accepts the floor in string and check if the floor has any number.
   * @input { string }
   * @param { str }
   * @output { boolean }
   */

  static stringContainsNumber(str: string) {
    return /\d/.test(str);
  }

  /**
   * @description: This method accept string and returns the Ordinal Suffix of that string number
   * @param { string } number pass a number as string
   * @output { string }
   */

  static ordinalSuffixConverter(number: string): string {
    const matcher: Array<string> = number.match(`1?.$`) ?? ['0'];
    number += [, 'st', 'nd', 'rd'][+matcher[0]] || 'th';
    return number;
  }

  /**
   * @description: #getUserRoles method will return available user roles
   * @output { IUserGroups } userRoles
   */
  static getUserRoles(translate: TranslateService): IUserGroups {
    const userRoles = [
      {
        label: translate.instant('PROPERTY_MANAGER_ADMIN'),
        name: 'group',
        value: 1,
        desc: translate.instant('DESCRIPTION_PROPERTY_MANAGER_ADMIN'),
        type: 'Property_Manager_Admin',
        active: false,
      },
      {
        label: translate.instant('PROPERTY_MANAGER_USER'),
        name: 'group',
        value: 2,
        desc: translate.instant('DESCRIPTION_PROPERTY_MANAGER_USER'),
        type: 'Property_Manager_User',
        active: false,
      },
      {
        label: translate.instant('INSTALLER_ADMIN'),
        name: 'group',
        value: 3,
        desc: translate.instant('DESCRIPTION_INSTALLER_ADMIN'),
        type: 'Installer_Admin',
        active: false,
      },
      {
        label: translate.instant('INSTALLER_USER'),
        name: 'group',
        value: 4,
        desc: translate.instant('DESCRIPTION_INSTALLER_USER'),
        type: 'Installer_User',
        active: false,
      },
      {
        label: translate.instant('RESIDENT'),
        name: 'group',
        value: 6,
        desc: translate.instant('DESCRIPTION_RESIDENT'),
        type: 'Resident',
        active: false,
      },
    ];
    return userRoles;
  }
  /**
   * @description: #getBatteryPercentage function recieves battery percentage in string and converts it to number.
   * @param { string } batteryPercentage
   * @returns { number } battery
   */

  static getBatteryPercentage(batteryPercentage: string): number {
    batteryPercentage = String(batteryPercentage)?.replace('%', '');
    return parseInt(batteryPercentage);
  }

  /**
   * @description: #getUnitActivityTypes method will return available unit activity types
   * @output { IActivityType } IActivityType
   */
  static getUnitActivityTypes(
    translate: TranslateService,
    isUserFilter = false,
    isUnitFilter = false
  ): IActivityType[] {
    const activities = [
      {
        label: '',
        eventType: translate.instant('LOW_BATTERY_ALARM'),
        eventCode: '0x07',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('VERY_LOW_BATTERY_ALARM'),
        eventCode: '0x08',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('TOO_LOW_TO_OPERATE'),
        eventCode: '0x09',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('NEW_BATTERY_INSERTED'),
        eventCode: '0x0A',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('DOOR_TAMPER_ALERT'),
        eventCode: '0x17',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('INVALID_NFC_ACCESS_CODE'),
        eventCode: '0x1C',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('ATTEMPTED_NFC_ENTRY_OUTSIDE_ACCESS_CODE'),
        eventCode: '0x1D',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('MANUALLY_NOT_FULLY_LOCK'),
        eventCode: '0x00',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('MANUALLY_UNLOCKED'),
        eventCode: '0x02',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('MANUALLY_LOCKED'),
        eventCode: '0x01',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('MOBILE_USER_LOCKED'),
        eventCode: '0x51',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('MOBILE_USER_UNLOCKED'),
        eventCode: '0x52',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('NFC_LOCKED'),
        eventCode: '0xE1',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('NFC_UNLOCKED'),
        eventCode: '0xE2',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: translate.instant('UNKNOWN'),
        eventCode: '0x06',
        active: false,
        image: 'ico-unlock.svg',
      },
    ];

    const excludedCodes = new Set([
      ...(isUserFilter
        ? [
            '0x07',
            '0x08',
            '0x09',
            '0x0A',
            '0x17',
            '0x1C',
            '0x1D',
            '0x00',
            '0x01',
            '0x02',
            '0x06',
          ]
        : []),
      ...(isUnitFilter ? ['0x17', '0x06'] : []),
    ]);

    return excludedCodes.size
      ? activities.filter(({ eventCode }) => !excludedCodes.has(eventCode))
      : activities;
  }

  /**
   * @description: #getAccessPointActivityTypes method will return available access point activity types
   * @output { IActivityType } IActivityType
   */
  static getAccessPointActivityTypes(): IActivityType[] {
    return [
      {
        label: '',
        eventType: 'Controller Online',
        eventCode: '10',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Controller Offline',
        eventCode: '11',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Reader Online',
        eventCode: '15',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Reader Offline',
        eventCode: '16',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Tamper',
        eventCode: '126',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Door Forced Open',
        eventCode: '52',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Access Granted',
        eventCode: '48',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Access Mode: No Access',
        eventCode: ' 61',
        active: false,
        image: 'ico-unlock.svg',
      },
      {
        label: '',
        eventType: 'Access Mode: Unlocked',
        eventCode: '60',
        active: false,
        image: 'ico-unlock.svg',
      },
    ];
  }
}

/**
 * @description: #addFlagIfMatched method add isDefault flag as true or false in an array based on condition that either user Group
 *  is default user group or simple user group and will return userGroupList
 * @output userGroupList
 */
export function addFlagIfMatched(
  dashboardUserList: UserCards,
  userGroupList: any
) {
  for (const groupListItem of userGroupList) {
    const { userGroupId } = groupListItem;
    const matchedGroup = dashboardUserList?.defaultUserGroups.find(
      (group) => group.userGroupId === userGroupId
    );
    if (matchedGroup) {
      // If there's a match, add the 'isDefault=true' flag to the current groupListItem
      groupListItem.isDefault = true;
    } else {
      groupListItem.isDefault = false;
    }
  }
  return userGroupList;
}
/**
 * @description this method returns language for user
 * @param langCode
 * @returns { string }
 */
export function getUILanguage(langCode: string): string {
  //please add this line for dynamically return language behavior --> lang || config.LANGUAGES["en-us"]
  return 'English';
}

/**
 * @description this method is for directly placing something to localStorage
 * @param { string } key
 * @param { string } value
 * @returns { void }
 */
export function saveCurrentState(key: string, value: string) {
  try {
    let currentState = localStorage.getItem('state');
    if (currentState) {
      let updatedState: PermanantStateManagement = {};
      updatedState = JSON.parse(currentState);
      updatedState[key] = value;
      localStorage.setItem('state', JSON.stringify(updatedState));
    } else {
      localStorage.setItem('state', JSON.stringify({ [key]: value }));
    }
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * @description this method is to get object directly from localStorage
 * @param { string } key
 * @returns { void }
 */

export function getCurrentState(key: string) {
  try {
    let currentState = localStorage.getItem('state');
    if (currentState) {
      let updatedState: PermanantStateManagement = {};
      updatedState = JSON.parse(currentState);
      return updatedState[key];
    }
  } catch (err) {
    return undefined;
  }
}

/**
 * @description this method will convertaccessibility matrix to a storable form
 * @param { string } role
 * @param { string } matrix
 * @returns { void }
 */
export function convertAccessibilityMatrix(role: string, matrix: any) {
  const userRole = role.replace(/_/g, '');
  const accessRights = [
    {
      name: PageName.Site,
      create: matrix?.addSite,
      update: matrix?.updateSite,
      view: matrix?.getSites,
      delete: matrix?.deleteSite,
    },
    {
      name: PageName.Building,
      create: matrix?.addBuilding,
      update: true,
      view: matrix?.getBuildingsBySite,
      delete: matrix?.deleteBuilding,
    },
    {
      name: PageName.Floor,
      create: matrix?.addFloor,
      update: matrix?.updateFloor,
      view: matrix?.getFloorsByBuilding,
      delete: matrix?.deleteFloor,
    },
    {
      name: PageName.Unit,
      create: true,
      update: true,
      view: true,
      delete: matrix?.deleteUnit,
    },
  ];
  return [userRole, accessRights];
}

/**
 *
 * @param time string time format
 * @param currentFormat converted default format given select any of one
 * @param parseFormat converted default format given select any of one
 * @returns string date / time format give
 */
export function convertParseDateTime(
  time: string | undefined,
  currentFormat:
    | 'hh:mm a'
    | 'hh:mma'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | 'yyyy, MM, dd h:mm a'
    | 't'
    | `yyyy/MM/dd hh:mm a`
    | `yyyy, MM, dd hh:mm a`
    | `yyyy-MM-dd\'T\'HH:mm:ss`
    | `MM/dd/yyyy, h:mm:ss a`
    | 'MM/dd/yyyy',
  parseFormat:
    | 'hh:mm a'
    | 'hh:mma'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | 'dd/MM/yyyy'
    | 'MM/dd/yyyy'
    | 'yyyy, MM, dd h:mm a'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | 't'
    | `yyyy, MM, dd hh:mm a`
    | `yyyy/MM/dd hh:mm a`
    | `yyyy-MM-dd\'T\'HH:mm:ss`
    | `MMM d, yyyy h:mma`
    | `MM/dd/yyyy, h:mm:ss a`
): string {
  if (!time) return '';
  const parsedDate = parse(time!, currentFormat, new Date());
  if (parseFormat === `yyyy-MM-dd\'T\'HH:mm:ss`) {
    return format(parsedDate, parseFormat);
  }
  return format(parsedDate, parseFormat).replace(/-/g, '/');
}

const localeFormatMap: Record<string, Record<string, string>> = {
  fr: {
    "EEE, MMM do, yyyy 'at' h:mm a": "EEE, MMM d, yyyy 'à' h:mm a",
    'MMM do, yyyy h:mm a': 'MMM d, yyyy h:mm a',
  },
};

/**
 *
 * @param time string time format
 * @param currentFormat converted default format given select any of one
 * @param parseFormat converted default format given select any of one
 * @returns string date / time format give
 */
export function convertParseDateTimeLangSpecific(
  time: string | undefined,
  currentFormat:
    | 'hh:mm a'
    | 'hh:mma'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | "EEE, MMM d, yyyy 'at' h:mm a"
    | 'yyyy, MM, dd h:mm a'
    | 't'
    | `yyyy/MM/dd hh:mm a`
    | `yyyy, MM, dd hh:mm a`
    | `yyyy-MM-dd\'T\'HH:mm:ss`
    | `MM/dd/yyyy, h:mm:ss a`
    | 'MM/dd/yyyy'
    | `EEEE`,
  parseFormat:
    | 'hh:mm a'
    | 'hh:mma'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | 'dd/MM/yyyy'
    | 'MM/dd/yyyy'
    | 'yyyy, MM, dd h:mm a'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | "EEE, MMM d, yyyy 'at' h:mm a"
    | 't'
    | `yyyy, MM, dd hh:mm a`
    | `yyyy/MM/dd hh:mm a`
    | `yyyy-MM-dd\'T\'HH:mm:ss`
    | `MMM d, yyyy h:mma`
    | `MM/dd/yyyy, h:mm:ss a`
    | `EEEE`,
  locale = enUS
): string {
  if (!time) return '';
  let mappedCurrentFormat = String(parseFormat);
  if (locale == fr) {
    const localeKey = locale === fr ? 'fr' : 'en';
    mappedCurrentFormat =
      localeFormatMap[localeKey]?.[parseFormat] || parseFormat;
  }
  if (parseFormat === 'EEEE') {
    return time
      .split(',')
      .map((day) => {
        const trimmedDay = day.trim();
        const parsed = parse(trimmedDay, currentFormat, new Date());
        return format(parsed, mappedCurrentFormat, { locale });
      })
      .join(', ');
  }
  const parsedDate = parse(time, currentFormat, new Date());
  if (mappedCurrentFormat === `yyyy-MM-dd\'T\'HH:mm:ss`) {
    return format(parsedDate, mappedCurrentFormat, { locale });
  }
  return format(parsedDate, mappedCurrentFormat, { locale }).replace(/-/g, '/');
}

/**
 *
 * @param time string time format
 * @param currentFormat converted default format given select any of one
 * @param parseFormat converted default format given select any of one
 * * @param yesterday default will be available yesterday
 * @returns string date / time format give
 */
export function activityParseDate(
  time: string,
  currentFormat: `MMM dd` | `MMM dd` | `MMM yyyy` | `yyyy-MM-dd\'T\'HH:mm:ss`,
  parseFormat: `t` | `MMM dd` | `MMM yyyy`,
  Yesterday: boolean = true,
  translate?: TranslateService,
  locale: Locale = enUS
): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const covertCurrentDate = convertDateTimeFormatLangSpecific(
    new Date().toString(),
    'yyyy/MM/dd',
    locale
  );
  const covertYesterdayDate = convertDateTimeFormatLangSpecific(
    yesterday.toString(),
    'yyyy/MM/dd',
    locale
  );
  const convertServerDate = convertParseDateTimeLangSpecific(
    time,
    "yyyy-MM-dd'T'HH:mm:ss",
    'yyyy/MM/dd',
    locale
  );
  if (covertCurrentDate === convertServerDate) {
    return translate?.instant(CONST.TODAY) ?? CONST.TODAY;
  } else if (covertYesterdayDate === convertServerDate && Yesterday) {
    return translate?.instant(CONST.YESTERDAY) ?? CONST.YESTERDAY;
  }
  return format(parse(time, currentFormat, new Date()), parseFormat, {
    locale,
  }).replace(/-/g, '/');
}
/**
 * @description It's reset form state with empty strin
 * @param formGroup formgroup instance
 */
export function resetForm(formGroup: FormGroup, value: any = '') {
  let control: AbstractControl;
  formGroup.reset();
  formGroup.markAsUntouched();
  Object.keys(formGroup.controls).forEach((name) => {
    control = formGroup.controls[name];
    control.patchValue(value);
    control.setErrors(null);
  });
}
/**
 *
 * @param time string time format
 * @param convertFormat converted default format given select any of one
 * @returns string date / time format given
 */
export function convertDateTimeFormat(
  time: string,
  convertFormat:
    | 'hh:mm a'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | 'yyyy, MM, dd h:mm a'
    | 't'
    | 'yyyy/MM/dd hh:mm a'
    | `yyyy-MM-dd\'T\'HH:mm:ss`
): string {
  const convertStringToDate = new Date(time);
  const getValidDate =
    convertStringToDate.toString() === 'Invalid Date'
      ? new Date(time.replace(/-/g, '/'))
      : convertStringToDate;
  return format(getValidDate, convertFormat);
}

/**
 *
 * @param time string time format
 * @param convertFormat converted default format given select any of one
 * @returns string date / time format given
 */
export function convertDateTimeFormatLangSpecific(
  time: string,
  convertFormat:
    | 'hh:mm a'
    | 'HH:mm'
    | 'yyyy-MM-dd'
    | 'yyyy/MM/dd'
    | "EEE, MMM do, yyyy 'at' h:mm a"
    | 'yyyy, MM, dd h:mm a'
    | 't'
    | 'yyyy/MM/dd hh:mm a'
    | `yyyy-MM-dd\'T\'HH:mm:ss`,
  locale: Locale = enUS
): string {
  const convertStringToDate = new Date(time);
  const getValidDate =
    convertStringToDate.toString() === 'Invalid Date'
      ? new Date(time.replace(/-/g, '/'))
      : convertStringToDate;
  return format(getValidDate, convertFormat, { locale });
}

/**
 *
 * @param startTime number
 * @param endTime number
 * @returns {object} {hours, minutes}
 * @description this function takes start time and end time and returns the object.
 */
export function getTimeDifference(startTime: number, endTime: number) {
  let delta = endTime - startTime;
  if (delta < 0) return { days: 0, hours: 0, minutes: 0 };

  let days = Math.floor(delta / 86400);
  delta -= days * 86400;

  let hours = Math.floor(delta / 3600);
  delta -= hours * 3600;

  let minutes = Math.floor(delta / 60);
  delta -= minutes * 60;

  return { days, hours, minutes };
}

/**
 *
 * @param data {string}
 * @description remove selected special character and return string
 * @returns string
 */
export function removeSpecialChar(data: string): string {
  return data.replace(/[()-\s]/g, '');
}
/**
 * @param data {string}
 * @description function remove special character from the number and return number
 */
export function transformPhone(data: string) {
  return data.replace(/[^+\d]+/g, '');
}

/**
 * @param data {event}
 * @param data {HTML Element}
 * @description function remove the last character of specific input field when backspace pressed and set the selected range of input
 */

export function onEnterBackSpace(ev: any, phoneInput: ElementRef): void {
  let startPosition = ev.target.selectionStart;

  if (ev.key === config.KEYBOARD_KEY.BACKSPACE) {
    if (startPosition >= 6) {
      setTimeout(() => {
        phoneInput.nativeElement.focus();
        phoneInput.nativeElement.setSelectionRange(
          startPosition - 1,
          startPosition - 1
        );
      }, 100);
    }
  }
}

/**
Extracts the date and time value from a given string.
@param dateString The input string containing the date and time value.
@returns The extracted date and time value in the format "yyyy-MM-dd\'T\'HH:mm:ss", or an empty string if no match is found.
*/
export function extractDateTimeFromDateString(dateString: string): string {
  const regex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/; //'2023-05-23T09:00:00Z UTC-07:00'
  const match = dateString.match(regex);

  if (match) {
    return match[1];
  }

  return ''; // or any appropriate default value
}

/**
Extracts the date and time value from a given string.
@param dateString The input string containing the date and time value.
@returns The extracted date and time value in the format "yyyy-MM-dd\'T\'HH:mm:ss", or an empty string if no match is found.
*/
export function extractDateTimeFromDateStringLangSpecific(
  dateString: string,
  currentFormat: string
): string {
  if (currentFormat !== "yyyy-MM-dd'T'HH:mm:ss") {
    const parsedDate = parse(dateString, currentFormat, new Date());
    dateString = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss");
  }
  const regex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/; //'2023-05-23T09:00:00Z UTC-07:00'
  const match = dateString.match(regex);

  if (match) {
    return match[1];
  }

  return ''; // or any appropriate default value
}

/**
 * Converts a string representation of a number into a numeric value.
 *
 * This utility function is designed to convert numeric strings into actual
 * numbers, making them suitable for calculations and comparisons.
 *
 * @param time A string representing the numeric value to be converted.
 * @returns The numeric value obtained by converting the input string, or NaN if conversion fails.
 */
export function convertNumber(time: string): number {
  return Number(time);
}

/**
 * Converts a number into a word.
 *
 * This utility function is designed to convert number into words.
 *
 * @param number A number to be converted.
 * @returns The word obtained by converting the input number, or NaN if conversion fails.
 */
export function convertNumberToWords(number: number): string {
  if (number === 0) return 'zero';
  const onesWords = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
  ];
  const teensWords = [
    '',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ];
  const tensWords = [
    '',
    'ten',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ];

  let words: string = '';
  if (number >= 1e9) {
    words += convertNumberToWords(Math.floor(number / 1e9)) + ' billion ';
    number %= 1e9;
  }
  if (number >= 1e6) {
    words += convertNumberToWords(Math.floor(number / 1e6)) + ' million ';
    number %= 1e6;
  }
  if (number >= 1000) {
    words += convertNumberToWords(Math.floor(number / 1000)) + ' thousand ';
    number %= 1000;
  }
  if (number >= 100) {
    words += onesWords[Math.floor(number / 100)] + ' hundred ';
    number %= 100;
  }
  if (number >= 20 || number == 10) {
    words += tensWords[Math.floor(number / 10)] + ' ';
    number %= 10;
  } else if (number >= 11) {
    words += teensWords[number - 10] + ' ';
    number = 0;
  }
  if (number > 0) {
    words += onesWords[number] + ' ';
  }
  return words.trim();
}

/* Converts the date time according to site timezone.
 *
 * @param timeZone A string representing the timezone of the site.
 * @returns The date value according to the site timezone.
 */
export function convertToSiteTimeZone(timeZone: string): Date {
  const date = new Date();
  const zonedTime = utcToZonedTime(date, timeZone);
  return zonedTime;
}
